import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CalendarDays, ChevronDown, Menu, Moon, Sun, UserCircle } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/misc';
import { useAppDispatch, useAppSelector, useAuth, useGoogleMapsEnabled } from '@/hooks';
import { selectTheme, setTheme } from '@/features/settings';
import { googleMapsApiSettings } from '@/services/googleMapsApiSettings';
import { formatDate, initials, cn } from '@/utils';

interface TopNavProps {
  onMenuClick: () => void;
}

export function TopNav({ onMenuClick }: TopNavProps): React.JSX.Element {
  const { user, logout } = useAuth();
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectTheme);
  const mapsEnabled = useGoogleMapsEnabled();
  const canUseGoogleMaps =
    Boolean(user?.enableGoogleApi) && Boolean(user?.googleMapsApiKey?.trim());
  const today = new Date().toISOString();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-card px-4 sm:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenuClick} aria-label="Open navigation">
        <Menu className="h-5 w-5" />
      </Button>
      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <span className="hidden items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-muted-foreground sm:flex">
          <CalendarDays className="h-4 w-4" aria-hidden="true" />
          {formatDate(today)}
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
        <label
          className={cn(
            'hidden items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-muted-foreground sm:flex',
            canUseGoogleMaps ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
          )}
        >
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={mapsEnabled}
            disabled={!canUseGoogleMaps}
            onChange={() => googleMapsApiSettings.toggle()}
          />
          Google Maps
        </label>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button type="button" className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-accent" aria-label="User menu">
              <Avatar>
                <AvatarFallback>{user ? initials(user.name) : '??'}</AvatarFallback>
              </Avatar>
              <span className="hidden text-left sm:block">
                <span className="block text-sm font-semibold">{user?.name ?? 'Guest'}</span>
                <span className="block text-xs text-muted-foreground">
                  {user ? `${user.title} · ${user.agentId}` : ''}
                </span>
              </span>
              <ChevronDown className="hidden h-4 w-4 text-muted-foreground sm:block" aria-hidden="true" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              className="z-50 min-w-44 rounded-md border bg-card p-1 text-sm shadow-md"
            >
              <DropdownMenu.Item asChild>
                <Link to="/profile" className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none hover:bg-accent">
                  <UserCircle className="h-4 w-4" /> Profile
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link to="/settings" className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 outline-none hover:bg-accent">
                  Settings
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                data-blocked-during-call
                className="cursor-pointer rounded-sm px-2 py-1.5 text-destructive outline-none hover:bg-destructive/10"
                onSelect={() => void logout()}
              >
                Logout
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
